import { useEffect, useRef, useState } from 'react'

import internalApiQueryWrapper from '../../../core/internalApiQueryWrapper'
import { useScript } from '../../../hooks/useScript'
import style from '../../../styles/Weather/weather_location'
import { formatLocation } from '../../../utils/formatLocation'
import { getDistance } from '../../../utils/getDistance'
import { useEnvironmentContext } from '../../EnvironmentProvider'
import { track } from '../../utilities/track'

import { useRouter } from 'next/router'

const isLocationOutsideSupportedArea = async location => {
  const nearestWeatherStation = await internalApiQueryWrapper('/api/contentful/weather', {
    content_type: 'weatherStation',
    'fields.location[near]': `${location.lat},${location.lon}`,
    include: 1,
    limit: 1
  })

  if (!nearestWeatherStation?.location) {
    return true
  }

  const distance = getDistance(location, nearestWeatherStation?.location)

  return distance > 30
}

const ChooseWeatherLocation = ({ close, locationChanged }) => {
  const router = useRouter()
  const { googlePlaceAPIKey } = useEnvironmentContext()
  const status = useScript(`https://maps.googleapis.com/maps/api/js?key=${googlePlaceAPIKey}&libraries=places`, {
    removeOnUnmount: true
  })

  const [scriptStatus, setScriptStatus] = useState(null)
  const [weatherLocationState, setWeatherLocationState] = useState('useCurrentLocation')

  const autoCompleteRef = useRef()
  const inputRef = useRef()
  const options = {
    componentRestrictions: { country: 'us' },
    fields: ['address_components', 'geometry', 'icon', 'name'],
    types: ['geocode']
  }

  const userChangeLocation = async newLocation => {
    setWeatherLocationState('loading')

    track('Weather_location', { location: newLocation })

    const value = await isLocationOutsideSupportedArea(newLocation)
    if (value) {
      setWeatherLocationState('locationOutsideSupportedArea')
    } else {
      locationChanged(newLocation)
    }
  }

  useEffect(() => {
    setScriptStatus(status)
    if (window?.google?.maps) {
      autoCompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, options)

      const eventHandler = async () => {
        const place = await autoCompleteRef.current.getPlace()

        if (place) {
          const location = {
            name: formatLocation(place),
            lat: place.geometry.location.lat(),
            lon: place.geometry.location.lng()
          }

          await userChangeLocation(location)
        }
      }
      autoCompleteRef?.current?.addListener('place_changed', eventHandler)
    }
  }, [status])

  useEffect(() => {
    return () => {
      google.maps.event.clearListeners(autoCompleteRef.current, 'place_changed')
      google.maps.event.clearInstanceListeners(autoCompleteRef.current)
    }
  }, [])

  const getCurrentPositionSuccess = position => {
    const geocoder = new window.google.maps.Geocoder()
    const latLng = new window.google.maps.LatLng(position.coords.latitude, position.coords.longitude)

    geocoder.geocode(
      {
        latLng: latLng
      },
      async (results, status) => {
        if (status !== 'OK') {
          setWeatherLocationState('useCurrentLocationError')
          return
        }
        const place = results[1]
        if (place) {
          const location = {
            name: formatLocation(place),
            lat: position.coords.latitude,
            lon: position.coords.longitude
          }

          await userChangeLocation(location)
        }
      }
    )
  }

  const getCurrentPositionError = () => {
    setWeatherLocationState('useCurrentLocationError')
    track('Disable_location')
  }

  const useCurrentLocation = () => {
    setWeatherLocationState('loading')
    navigator.geolocation.getCurrentPosition(getCurrentPositionSuccess, getCurrentPositionError)
    track('Use_Current_Location_Click', {})
  }

  const renderWeatherLocationState = state => {
    switch (state) {
      case 'loading':
        return (
          <>
            <div className='use-current-location'>Loading...</div>
            <style jsx>{style}</style>
          </>
        )
      case 'useCurrentLocation':
        if (router.query?.isMobile) {
          return <></>
        }
        return (
          <>
            <button className='use-current-location' onClick={() => useCurrentLocation()}>
              <img className='edit-icon' src='/current-location.svg' alt='' /> Use current location
            </button>
            <style jsx>{style}</style>
          </>
        )
      case 'useCurrentLocationError':
        return (
          <>
            <div className='use-current-location error'>Browser settings have disabled location services.</div>
            <style jsx>{style}</style>
          </>
        )
      case 'locationOutsideSupportedArea':
        return (
          <>
            <div className='use-current-location error'>Location outside of supported area.</div>
            <style jsx>{style}</style>
          </>
        )
    }
  }

  return (
    <>
      <div className='choose-location-wrapper'>
        <button className='close-icon' onClick={() => close()}>
          <img src='/close_icon_gray.svg' alt='' />
        </button>
        <label className='autocomplete'>
          <input
            data-testid='choose-weather-input'
            disabled={scriptStatus === 'loading'}
            placeholder={scriptStatus === 'loading' ? 'Loading...' : 'City, Town or Zip Code'}
            ref={inputRef}
            type='search'
          />
        </label>

        {renderWeatherLocationState(weatherLocationState)}
      </div>
      <style jsx>{style}</style>
    </>
  )
}

export default ChooseWeatherLocation
